import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { COLOR } from '@src/styles/color';
import { PROJECT_PRODUCT } from '@src/constants/project';
import { MOE_PROJECT_SOURCE_TYPE } from '@src/constants/moengage';
import apis from '@src/apis';
import actions from '@src/redux/actions';
import debounce from '@src/utils/debounce';
import { isValidHttpUrl } from '@src/utils/checkValid';
import {
  buildDefaultTitle,
  buildTtsBlocksFromText,
} from '@src/services/projectInput';
import { useCreateProjectFromInput } from '@src/hooks/useCreateProjectFromInput';
import CommonDialog from '@src/new-components/Dialog/CommonDialog';
import CommonButton from '@src/new-components/Button';
import UrlInputField from './UrlInputField';
import UrlSuggestedSources from './UrlSuggestedSources';
import UrlPreviewItem from './UrlPreviewItem';
import { StyledUrlBody, StyledUrlErrorText } from './index.style';

const ImportProjectUrlPopup = ({ open, onClose }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { createProject, finalizeProjectDefault } = useCreateProjectFromInput();

  const [url, setUrl] = useState('');
  const [preview, setPreview] = useState(null);
  const [fetching, setFetching] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Reset on close.
  useEffect(() => {
    if (open) return;
    setUrl('');
    setPreview(null);
    setFetching(false);
    setError('');
    setSubmitting(false);
  }, [open]);

  // Debounced crawl: legacy uses `@src/utils/debounce` returning a debounced
  // closure. Capture the latest result through a cancel flag so a stale
  // response can't overwrite the current input state.
  const handleCrawl = useCallback(
    async (link, cancelRef) => {
      if (!isValidHttpUrl(link)) {
        setPreview(null);
        return;
      }
      setFetching(true);
      setError('');
      try {
        const data = await apis.upload.crawlNewsByUrl(link);
        if (cancelRef.cancelled) return;
        if (data?.status && data.results) {
          setPreview(data.results);
        } else {
          setError(t('errorHandleByUrl'));
          setPreview(null);
        }
      } catch (e) {
        if (!cancelRef.cancelled) {
          setError(e?.message || t('errorHandleByUrl'));
          setPreview(null);
        }
      } finally {
        if (!cancelRef.cancelled) setFetching(false);
      }
    },
    [t],
  );

  useEffect(() => {
    if (!url) {
      setPreview(null);
      setError('');
      return undefined;
    }
    const cancelRef = { cancelled: false };
    debounce(handleCrawl, 500)(url, cancelRef);
    return () => {
      cancelRef.cancelled = true;
    };
  }, [url, handleCrawl]);

  const handleRemove = () => {
    setUrl('');
    setPreview(null);
    setError('');
  };

  const handleSubmit = async () => {
    if (!preview) return;
    setSubmitting(true);
    try {
      const { projectId } = await createProject({
        title: preview.title || buildDefaultTitle(t),
        product: PROJECT_PRODUCT.TTS,
        blocks: buildTtsBlocksFromText(preview.text || ''),
        sourceType: MOE_PROJECT_SOURCE_TYPE.URL,
      });
      onClose?.();
      finalizeProjectDefault({ projectId, product: PROJECT_PRODUCT.TTS });
    } catch (e) {
      const msg =
        e?.code === 'create-failed' ? 'createProjectFailed' : 'uploadError';
      dispatch(actions.noti.push({ severity: 'error', message: msg }));
    } finally {
      setSubmitting(false);
    }
  };

  const submitDisabled = !preview || fetching || submitting;

  return (
    <CommonDialog
      open={open}
      onClose={onClose}
      title={t('webLink')}
      width="640px"
      disableAutoClose
      disableClose={submitting}
      contentComponent={
        <StyledUrlBody>
          {preview ? (
            <UrlPreviewItem preview={preview} onRemove={handleRemove} />
          ) : (
            <>
              <UrlInputField
                value={url}
                onChange={setUrl}
                loading={fetching}
                placeholder={t('exampleValidUrl')}
              />
              {!!error && <StyledUrlErrorText>{error}</StyledUrlErrorText>}
              <UrlSuggestedSources />
            </>
          )}
        </StyledUrlBody>
      }
      actionComponent={
        <CommonButton
          variant="contained"
          bgrDisabled={COLOR.yellow.default}
          customStyle={{
            height: 40,
            padding: '8px 24px',
            borderRadius: '8px',
            minWidth: 'auto',
            '&:disabled': { opacity: 0.32, color: COLOR.darkBlue },
          }}
          textProps={{ variantText: 'body-14-semibold', color: COLOR.darkBlue }}
          disabled={submitDisabled}
          onClick={handleSubmit}
        >
          {t('continue')}
        </CommonButton>
      }
    />
  );
};

export default ImportProjectUrlPopup;
